import { Navigate, RouteObject } from "react-router-dom";
import { routeCan, routeCanAny } from "src/authentication/routeCan";
import { Action } from "src/casl/Action";
import { goToFirstTab } from "src/core/components/HeadedLayout";
import { ministrySeasonEventsRoutes } from "./ministry-seasons/events/ministrySeasonEventsRoutes";
import { subject } from "@casl/ability";

export const ministriesRoutes: RouteObject[] = [
    {
        children: [
            { index: true, lazy: () => import("./dashboard/MinistriesDashboardScreen") },

            {
                path: ":ministryId",
                handle: routeCan(Action.Read, "Ministry"),
                lazy: () => import("./view-ministry/MinistryLayout"),
                children: [
                    { index: true, handle: goToFirstTab },

                    {
                        path: "ministry-seasons",
                        children: [
                            { index: true, lazy: () => import("./view-ministry/seasons/MinistrySeasonsTab") },

                            /**
                             * A season's own address is the tab showing it. The index route ranks
                             * this above the season's screens below, whose layout shares the path.
                             */
                            {
                                path: ":ministrySeasonId",
                                children: [
                                    {
                                        index: true,
                                        handle: routeCan(Action.Read, "MinistrySeason"),
                                        lazy: () => import("./view-ministry/seasons/MinistrySeasonsTab"),
                                    },
                                ],
                            },
                        ],
                    },

                    {
                        path: "migrations",
                        handle: routeCan(Action.Update, ({ ministryId }) => subject("Ministry", { id: ministryId })),
                        lazy: () => import("./view-ministry/migrations/MinistryRegistrationMigrationsTab"),
                    },

                    /** In place of Teams and Positions, so it is for whoever could open either */
                    {
                        path: "people",
                        handle: routeCanAny(
                            {
                                i: Action.Update,
                                a: ({ ministryId }) => subject("MinistryTeam", { ministry: { id: ministryId } }),
                            },
                            { i: Action.Update, a: ({ ministryId }) => subject("Ministry", { id: ministryId }) }
                        ),
                        lazy: () => import("./view-ministry/people/MinistryPeopleTab"),
                    },

                    { path: "teams", element: <Navigate to="../people" replace /> },
                    { path: "positions", element: <Navigate to="../people" replace /> },

                    {
                        path: "configuration",
                        handle: routeCan(Action.Manage, "all"),
                        lazy: () => import("./view-ministry/configuration/MinistryConfigurationTab"),
                    },
                ],
            },

            // A team's own screen and its tabs, before People took them over
            {
                path: ":ministryId/teams/:ministryTeamId/*",
                lazy: () => import("./view-ministry/people/OldTeamLinkRedirect"),
            },

            /** MINISTRY SEASONS */
            {
                path: ":ministryId/ministry-seasons/:ministrySeasonId",

                handle: routeCan(Action.Read, "MinistrySeason"),
                lazy: () => import("./ministry-seasons/MinistrySeasonLayout"),

                /** The season itself is drawn by the Seasons tab above, so there is no index here */
                children: [
                    {
                        path: "events",
                        handle: routeCan(Action.Read, "CalendarEvent"),

                        children: ministrySeasonEventsRoutes,
                    },

                    {
                        path: "duties",
                        handle: routeCan(Action.Read, "CalendarEvent"),
                        lazy: () => import("./ministry-seasons/duties/MinistrySeasonDutiesScreen"),
                    },

                    {
                        path: "attendance",
                        handle: routeCan(Action.Read, "CalendarEventAttendance"),
                        lazy: () => import("./ministry-seasons/attendance/MinistrySeasonAttendanceOverviewScreen"),
                    },
                    {
                        path: "registrations",
                        handle: routeCan(Action.Read, "MinistrySeasonRegistration"),
                        children: [
                            {
                                index: true,
                                lazy: () => import("./ministry-seasons/registrants/MinistrySeasonRegistrationsScreen"),
                            },

                            {
                                path: ":ministrySeasonRegistrationId",
                                lazy: () => import("./ministry-seasons/registrants/MinistrySeasonRegistrationLayout"),
                                children: [
                                    {
                                        index: true,
                                        lazy: () =>
                                            import(
                                                "./ministry-seasons/registrants/tabs/MinistrySeasonRegistrationSummaryTab"
                                            ),
                                    },
                                    {
                                        path: "contacts",
                                        lazy: () =>
                                            import(
                                                "./ministry-seasons/registrants/tabs/MinistrySeasonRegistrationContactsTab"
                                            ),
                                    },
                                    {
                                        path: "answers",
                                        lazy: () =>
                                            import(
                                                "./ministry-seasons/registrants/tabs/MinistrySeasonRegistrationAnswersTab"
                                            ),
                                    },
                                    {
                                        path: "attendance",
                                        lazy: () =>
                                            import(
                                                "./ministry-seasons/registrants/tabs/MinistrySeasonRegistrationAttendanceTab"
                                            ),
                                    },
                                ],
                            },
                        ],
                    },
                    {
                        path: "rosters",
                        handle: routeCan(Action.Read, "MinistrySeasonAttendanceRoster"),
                        lazy: () => import("./ministry-seasons/attendance-rosters/MinistrySeasonAttendanceRostersScreen"),

                        /** Nothing of their own to render: the screen reads its selection from the path */
                        children: [{ index: true }, { path: "not-on-a-roster" }, { path: ":ministrySeasonAttendanceRosterId" }],
                    },
                    {
                        path: "awards",
                        handle: routeCan(Action.Update, ({ ministrySeasonId }) =>
                            subject("MinistryAward", { ministrySeasonId })
                        ),
                        lazy: () => import("./ministry-seasons/awards/MinistrySeasonAwardsScreen"),
                    },

                    {
                        path: "bus-lists",
                        handle: routeCan(Action.Read, "VehicleStopList"),
                        lazy: () =>
                            import("./ministry-seasons/vehicle-stop-lists/ViewMinistrySeasonVehicleStopListsScreen"),
                    },

                    {
                        path: "configuration",
                        handle: routeCan(Action.Update, "MinistrySeason"),
                        lazy: () => import("./ministry-seasons/configuration/MinistrySeasonConfigurationScreen"),
                    },

                    {
                        path: "duties/edit",
                        handle: routeCan(Action.Update, "MinistryTeamEventDutyAssignment"),
                        lazy: () => import("./ministry-seasons/duties/edit-duties/EditDutiesScreenOutlet"),

                        children: [
                            {
                                index: true,

                                lazy: () => import("./ministry-seasons/duties/edit-duties/EditDutiesTeamListScreen"),
                            },

                            {
                                path: ":ministryTeamId",
                                lazy: () => import("./ministry-seasons/duties/edit-duties/EditDutiesTeamScreen"),

                                children: [
                                    {
                                        index: true,
                                        lazy: () => import("./ministry-seasons/duties/edit-duties/rotas/RotasTab"),
                                    },

                                    {
                                        path: "away-dates",
                                        lazy: () =>
                                            import("./ministry-seasons/duties/edit-duties/away-dates/AwayDatesTab"),
                                    },
                                ],
                            },
                        ],
                    },

                    {
                        path: "bus-lists/view-list/:vehicleStopListId",
                        handle: routeCan(Action.Read, "VehicleStopList"),
                        lazy: () =>
                            import(
                                "./ministry-seasons/vehicle-stop-lists/view-list/ViewMinistrySeasonVehicleStopListScreen"
                            ),
                    },
                ],
            },
        ],
    },
];
